import { ChannelUnsubscribe, channelClient, RequestPolicies, ContractEventOf } from 'shared/channels';
import { createEntityClient, runQueuedMutation } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { levelContract } from './levelContract';
import type { LevelCreateModel, LevelEditModel, LevelModel } from './types/model';

type LevelUpdatedEvent = ContractEventOf<typeof levelContract, DomainMessagesTypes.updatedEntity>;

/**
 * Levels as served by the service worker. Changes resolve once the server
 * accepted them and reject with its error (`ChannelRequestError.messageId`).
 */
export const levelClient = {
  ...createEntityClient(levelContract),

  /** One item for the views that show it, archived ones included; rejects when there is none. */
  getOneById(id: string): Promise<LevelModel> {
    return channelClient.request(levelContract, 'getOneById', { id }, RequestPolicies.read);
  },

  async create(level: LevelCreateModel): Promise<void> {
    await runQueuedMutation(
      levelContract,
      'create',
      (uniqueId) => ({ level, uniqueId }),
      DomainMessagesTypes.uploadedEntity
    );
  },

  async edit(level: LevelEditModel): Promise<void> {
    await runQueuedMutation(
      levelContract,
      'edit',
      (uniqueId) => ({ level, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },

  async remove(levelId: string, projectId: string): Promise<void> {
    await runQueuedMutation(
      levelContract,
      'remove',
      (uniqueId) => ({ levelId, projectId, uniqueId }),
      DomainMessagesTypes.deletedEntity
    );
  },

  async restore(levelId: string, projectId: string): Promise<void> {
    await runQueuedMutation(
      levelContract,
      'restore',
      (uniqueId) => ({ levelId, projectId, uniqueId }),
      DomainMessagesTypes.uploadedEntity
    );
  },

  onUpdated(handler: (event: LevelUpdatedEvent) => void): ChannelUnsubscribe {
    return channelClient.onEventOfType(levelContract, DomainMessagesTypes.updatedEntity, handler);
  },

  /**
   * Resolves with the next level update, rejects on an update error, on a
   * level change the server rejected (`failUploadedEntity`, at once instead of
   * after the timeout) or after the mutation timeout. Call it before the change
   * that causes the update.
   */
  waitForUpdate(): Promise<void> {
    return new Promise((resolve, reject) => {
      const stop = (): void => {
        clearTimeout(timer);
        stopUpdates();
        stopFailures();
      };
      const timer = setTimeout(() => {
        stop();
        reject(new Error('No level update within the mutation timeout'));
      }, RequestPolicies.mutation.timeoutMs);
      const stopUpdates = levelClient.onUpdated((event) => {
        if (!event.error && !event.data) {
          return;
        }
        stop();
        if (event.error) {
          reject(event.error);
        } else {
          resolve();
        }
      });
      const stopFailures = channelClient.onEventOfType(levelContract, DomainMessagesTypes.failUploadedEntity, (event) => {
        stop();
        reject(event.error);
      });
    });
  },
};
